import { useEffect, useRef, useCallback } from "react";
import useHashLocation from "../use-hash-location";
import { useSnapshot } from "valtio";
import store from "../appStore";
import { useTranslation } from "react-i18next";
import { useSpring, animated } from "@react-spring/web";
import { config } from "@react-spring/three";

const DEFAULT_TIMEOUT = 10000;

export const Autoplay = ({ path }: { path: string }) => {
  const { autoPlay, autoPlayTimeline, autoPlayCurrentIndex } = useSnapshot(store);
  const [_loc, navigate] = useHashLocation();

  const timerRef = useRef<NodeJS.Timeout>();
  const logIntervalRef = useRef<NodeJS.Timeout>();
  const startTimeRef = useRef<number>(0);

  const stopAutoplay = useCallback(() => {
    store.autoPlay = false;
    clearTimeout(timerRef.current);
    clearInterval(logIntervalRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    if (store.autoPlay) {
      stopAutoplay();
    }
  }, [stopAutoplay]);

  // Listen for user interactions to interrupt autoplay
  useEffect(() => {
    if (!autoPlay) return;

    const interactionEvents = ["mousedown", "keydown", "touchstart", "wheel", "scroll", "pointerdown"];
    interactionEvents.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));

    return () => {
      interactionEvents.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [autoPlay, resetTimer]);

  // Autoplay logic
  useEffect(() => {
    if (!autoPlay || !autoPlayTimeline) return;

    if (autoPlayCurrentIndex >= autoPlayTimeline.length) {
      store.autoPlayCurrentIndex = 0;
      return;
    }

    const currentStep = autoPlayTimeline[autoPlayCurrentIndex];
    const timeout = currentStep.time || DEFAULT_TIMEOUT;
    //console.log({ route: currentStep.route, timeout });
    navigate(currentStep.route);

    // Start timer logging
    startTimeRef.current = Date.now();
    logIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      //console.log(`Step ${autoPlayCurrentIndex + 1} (${currentStep.route}): ${elapsed}s elapsed`);
    }, 1000);

    timerRef.current = setTimeout(() => {
      clearInterval(logIntervalRef.current);
      store.autoPlayCurrentIndex += 1;
    }, timeout);
    //store.sideBarVisible = false;
    return () => {
      clearTimeout(timerRef.current);
      clearInterval(logIntervalRef.current);
    };
  }, [autoPlay, autoPlayTimeline, autoPlayCurrentIndex, navigate]);

  const handleStart = useCallback(async () => {
    if (!autoPlayTimeline) return;
    store.autoPlayCurrentIndex = 0;
    store.autoPlay = true;
  }, [autoPlayTimeline]);

  if (!autoPlayTimeline) return null;

  return (
    <div style={{}}>
      {!autoPlay ? (
        <span onClick={handleStart} id={"journey-1"}>
          Autoplay
        </span>
      ) : (
        <>
          <span onClick={stopAutoplay}>
            Stop Autoplay ({autoPlayCurrentIndex + 1}/{autoPlayTimeline?.length || 0})
          </span>
        </>
      )}
    </div>
  );
};

export const AutoplayProgress = () => {
  const { autoPlay, autoPlayTimeline, autoPlayCurrentIndex } = useSnapshot(store);
  const { t } = useTranslation();

  const progressSpring = useSpring({
    from: { width: "0%" },
    to: { width: "100%" },
    config: { ...config.stiff, duration: (autoPlayTimeline?.[autoPlayCurrentIndex]?.time || DEFAULT_TIMEOUT) - 500 }, //sub to give some buffer
    reset: true,
    key: autoPlayCurrentIndex,
    onChange: (result) => {
      // Logs the current animated width value on every frame
      //console.log("Current width:", result.value.width);
    },
  });

  if (!autoPlay) return null;

  return (
    <div
      style={{
        position: "absolute",
        bottom: "1rem",
        left: "50%",
        transform: "translateX(-50%)",
      }}
    >
      <div
        style={{
          position: "relative",
          display: "block",
          marginBottom: ".6rem",
          width: "100%",
          textAlign: "center",
          fontSize: "0.9vw",
          fontWeight: 500,
          color: "white",
        }}
      >
        {t("PLEASE_INTERACT")}
      </div>
      <div
        style={{
          borderRadius: ".2rem",
          border: "1px solid #37a46e",
          width: "33vw",
          height: ".2rem",
          padding: 0,
          position: "relative",
        }}
      >
        <animated.span
          key={autoPlayCurrentIndex}
          style={{
            width: progressSpring.width,
            backgroundColor: "#37a46e",
            display: "inline-block",
            height: "100%",
            transition: "width 1s ease",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        ></animated.span>
      </div>
    </div>
  );
};
